import { EXIT_REASON_LABELS, isExitReason } from '@einhaus/project314-shared';
import FormInput from '@/components/UI/FormInput';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import { parseNumericDraft, toNumericDraft } from '@/components/UI/numericDraft';
import type { ContextFeatureControlType } from '@/types/appRouter';
import type { TradeLogicRegistrySchemaFieldDto } from '../registry';
import { schemaAllowedValueToInputString, schemaValueToInputString } from './schemaControlUtils';

interface SchemaFieldInputProps {
    field: TradeLogicRegistrySchemaFieldDto;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

const NUMERIC_CONTROL_TYPES: ReadonlySet<ContextFeatureControlType> = new Set(['number', 'percent', 'money']);
const TEXT_CONTROL_TYPES: ReadonlySet<ContextFeatureControlType> = new Set(['text', 'levelKey', 'duration']);

const SchemaFieldInput = ({ field, value, onChange, disabled = false }: SchemaFieldInputProps) => {
    const helpText = field.description ?? undefined;
    const label = field.required ? `${field.label} *` : field.label;
    const placeholder = resolvePlaceholder(field);
    const controlType = resolveControlType(field);

    if (controlType === 'boolean') {
        return (
            <FormSelect
                disabled={disabled}
                helpText={helpText}
                label={label}
                options={[
                    { value: '', label: field.required ? 'Select value' : 'Default' },
                    { value: 'true', label: 'True' },
                    { value: 'false', label: 'False' }
                ]}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (controlType === 'select' && field.allowedValues.length > 0) {
        return (
            <FormSelect
                disabled={disabled}
                helpText={helpText}
                label={label}
                options={[
                    { value: '', label: field.required ? 'Select value' : 'Default' },
                    ...field.allowedValues.map((allowedValue) => ({
                        value: schemaAllowedValueToInputString(allowedValue),
                        label:
                            field.key === 'reason' && isExitReason(allowedValue) ? EXIT_REASON_LABELS[allowedValue] : String(allowedValue)
                    }))
                ]}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (controlType === 'multiSelect') {
        const multiSelectHelp = helpText
            ? `${helpText} Enter as comma-separated values.`
            : `Enter as comma-separated values${field.allowedValues.length > 0 ? ` from: ${field.allowedValues.join(', ')}` : ''}.`;

        return (
            <FormInput
                disabled={disabled}
                helpText={multiSelectHelp}
                label={label}
                placeholder={placeholder ?? 'value1, value2'}
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (NUMERIC_CONTROL_TYPES.has(controlType)) {
        return renderNumericInput({ field, value, onChange, disabled, helpText, label, placeholder });
    }

    if (controlType === 'json') {
        return (
            <FormInput
                disabled={disabled}
                helpText={helpText ?? 'Enter valid JSON.'}
                label={label}
                placeholder={placeholder ?? '{ ... }'}
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (TEXT_CONTROL_TYPES.has(controlType)) {
        return (
            <FormInput
                disabled={disabled}
                helpText={helpText}
                label={label}
                placeholder={placeholder}
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (field.allowedValues.length > 0) {
        return (
            <FormSelect
                disabled={disabled}
                helpText={helpText}
                label={label}
                options={[
                    { value: '', label: field.required ? 'Select value' : 'Default' },
                    ...field.allowedValues.map((allowedValue) => ({
                        value: schemaAllowedValueToInputString(allowedValue),
                        label:
                            field.key === 'reason' && isExitReason(allowedValue) ? EXIT_REASON_LABELS[allowedValue] : String(allowedValue)
                    }))
                ]}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (field.type === 'boolean') {
        return (
            <FormSelect
                disabled={disabled}
                helpText={helpText}
                label={label}
                options={[
                    { value: '', label: field.required ? 'Select value' : 'Default' },
                    { value: 'true', label: 'True' },
                    { value: 'false', label: 'False' }
                ]}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    if (field.type === 'number' || field.type === 'integer' || field.type === 'timestamp') {
        return renderNumericInput({ field, value, onChange, disabled, helpText, label, placeholder });
    }

    return (
        <FormInput
            disabled={disabled}
            helpText={helpText}
            label={label}
            placeholder={placeholder}
            type="text"
            value={value}
            onChange={(event) => onChange(event.target.value)}
        />
    );
};

interface NumericInputArgs {
    field: TradeLogicRegistrySchemaFieldDto;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    helpText: string | undefined;
    label: string;
    placeholder: string | undefined;
}

/**
 * Schema field values are carried as strings, so the numeric control parses on the way out and
 * formats on the way in. The input keeps its own in-progress text, so a cleared field stays empty.
 */
function renderNumericInput({ field, value, onChange, disabled, helpText, label, placeholder }: NumericInputArgs) {
    const numericProps: { min?: number; max?: number; step?: number } = {};

    if (field.min !== null) numericProps.min = field.min;
    if (field.max !== null) numericProps.max = field.max;
    if (field.step !== null) numericProps.step = field.step;

    return (
        <FormNumberInput
            disabled={disabled}
            helpText={helpText}
            integer={field.type === 'integer' || field.type === 'timestamp'}
            label={label}
            placeholder={placeholder}
            value={parseNumericDraft(value)}
            onValueChange={(parsed) => onChange(toNumericDraft(parsed))}
            {...numericProps}
        />
    );
}

function resolvePlaceholder(field: TradeLogicRegistrySchemaFieldDto): string | undefined {
    if (field.placeholder !== null && field.placeholder.length > 0) return field.placeholder;
    if (field.examples[0] != null) return schemaValueToInputString(field.examples[0]);
    return undefined;
}

function resolveControlType(field: TradeLogicRegistrySchemaFieldDto): ContextFeatureControlType {
    if (field.controlType) return field.controlType;
    if (field.allowedValues.length > 0) return 'select';
    if (field.type === 'boolean') return 'boolean';
    if (field.type === 'number' || field.type === 'integer' || field.type === 'timestamp') return 'number';
    if (field.type === 'duration') return 'duration';
    if (field.type === 'json') return 'json';
    if (field.type === 'levelKey') return 'levelKey';
    return 'text';
}

export default SchemaFieldInput;
